/**
 * Session Management Service
 * Handles user session lifecycle, expiration, and activity tracking
 */

import { auth } from './firebase';
import { signOutUser } from './auth';
import { logger } from '@/utils/logger';

export interface SessionConfig {
  timeoutMinutes: number;
  warningMinutes: number;
  checkIntervalMinutes: number;
  trackActivity: boolean;
}

export interface SessionInfo {
  isActive: boolean;
  lastActivity: Date | null;
  expiresAt: Date | null;
  timeRemaining: number; // in milliseconds
}

/**
 * Session Manager handles user session lifecycle
 */
export class SessionManager {
  private static instance: SessionManager;
  private config: SessionConfig;
  private checkInterval: number | null = null;
  private warningShown: boolean = false;
  private activityListeners: (() => void)[] = [];
  private onSessionExpired?: () => void;
  private onSessionWarning?: (timeRemaining: number) => void;

  private constructor(config?: Partial<SessionConfig>) {
    this.config = {
      timeoutMinutes: 30, // 30 minutes default
      warningMinutes: 5, // Warn 5 minutes before expiration
      checkIntervalMinutes: 1, // Check every minute
      trackActivity: true,
      ...config,
    };

    this.initializeActivityTracking();
  }

  public static getInstance(config?: Partial<SessionConfig>): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager(config);
    }
    return SessionManager.instance;
  }

  /**
   * Start session monitoring
   */
  public startSession(
    onExpired?: () => void,
    onWarning?: (timeRemaining: number) => void
  ): void {
    this.onSessionExpired = onExpired;
    this.onSessionWarning = onWarning;

    this.updateLastActivity();
    this.startMonitoring();

    logger.info('Session monitoring started', {
      timeout: this.config.timeoutMinutes,
      warning: this.config.warningMinutes,
    });
  }

  /**
   * Stop session monitoring
   */
  public stopSession(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.removeActivityListeners();
    this.clearSessionData();
    this.warningShown = false;

    logger.info('Session monitoring stopped');
  }

  /**
   * Update last activity timestamp
   */
  public updateLastActivity(): void {
    const now = Date.now();
    localStorage.setItem('lastActivity', now.toString());
    this.warningShown = false; // Reset warning flag on activity

    if (auth.currentUser) {
      logger.debug('Session activity updated');
    }
  }

  /**
   * Get current session information
   */
  public getSessionInfo(): SessionInfo {
    const lastActivityStr = localStorage.getItem('lastActivity');
    const lastActivity = lastActivityStr
      ? new Date(parseInt(lastActivityStr))
      : null;

    if (!lastActivity) {
      return {
        isActive: false,
        lastActivity: null,
        expiresAt: null,
        timeRemaining: 0,
      };
    }

    const expiresAt = new Date(
      lastActivity.getTime() + this.config.timeoutMinutes * 60 * 1000
    );
    const timeRemaining = expiresAt.getTime() - Date.now();
    const isActive = timeRemaining > 0 && !!auth.currentUser;

    return {
      isActive,
      lastActivity,
      expiresAt,
      timeRemaining: Math.max(0, timeRemaining),
    };
  }

  /**
   * Check if session is expired
   */
  public isSessionExpired(): boolean {
    const sessionInfo = this.getSessionInfo();
    return sessionInfo.timeRemaining <= 0;
  }

  /**
   * Check if session is about to expire (within warning period)
   */
  public isSessionExpiring(): boolean {
    const sessionInfo = this.getSessionInfo();
    const warningThreshold = this.config.warningMinutes * 60 * 1000;
    return (
      sessionInfo.timeRemaining > 0 &&
      sessionInfo.timeRemaining <= warningThreshold
    );
  }

  /**
   * Extend session (reset activity timer)
   */
  public extendSession(): void {
    this.updateLastActivity();
    this.warningShown = false;
    logger.info('Session extended');
  }

  /**
   * Force session expiration
   */
  public async expireSession(
    reason: string = 'Manual expiration'
  ): Promise<void> {
    logger.info('Session expired', { reason });
    await this.handleExpiration();
  }

  /**
   * Initialize activity tracking
   */
  private initializeActivityTracking(): void {
    if (!this.config.trackActivity) return;

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    const activityHandler = () => {
      if (auth.currentUser) {
        this.updateLastActivity();
      }
    };

    // Throttle activity updates to avoid too frequent updates
    let lastUpdate = 0;
    const throttledHandler = () => {
      const now = Date.now();
      if (now - lastUpdate > 60000) {
        // Update at most once per minute
        lastUpdate = now;
        activityHandler();
      }
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, throttledHandler, true);
      this.activityListeners.push(() =>
        document.removeEventListener(event, throttledHandler, true)
      );
    });
  }

  /**
   * Remove activity listeners
   */
  private removeActivityListeners(): void {
    this.activityListeners.forEach(removeListener => removeListener());
    this.activityListeners = [];
  }

  /**
   * Start monitoring session
   */
  private startMonitoring(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = window.setInterval(
      () => {
        this.checkSession();
      },
      this.config.checkIntervalMinutes * 60 * 1000
    );
  }

  /**
   * Check session status
   */
  private checkSession(): void {
    if (!auth.currentUser) {
      this.stopSession();
      return;
    }

    const sessionInfo = this.getSessionInfo();

    if (sessionInfo.timeRemaining <= 0) {
      // Session expired
      this.handleExpiration();
    } else if (this.isSessionExpiring() && !this.warningShown) {
      // Session about to expire
      this.handleWarning(sessionInfo.timeRemaining);
    }
  }

  /**
   * Handle session expiration
   */
  private async handleExpiration(): Promise<void> {
    this.stopSession();

    try {
      await signOutUser();
      logger.auth.sessionExpired();

      if (this.onSessionExpired) {
        this.onSessionExpired();
      } else {
        // Default behavior: redirect to login with session expired message
        const currentPath = window.location.pathname;
        window.location.href = `/login?sessionExpired=true&returnTo=${encodeURIComponent(currentPath)}`;
      }
    } catch (error) {
      logger.error('Error during session expiration:', error);
    }
  }

  /**
   * Handle session warning
   */
  private handleWarning(timeRemaining: number): void {
    this.warningShown = true;
    logger.warn('Session expiring soon', { timeRemaining });

    if (this.onSessionWarning) {
      this.onSessionWarning(timeRemaining);
    }
  }

  /**
   * Clear session data from storage
   */
  private clearSessionData(): void {
    localStorage.removeItem('lastActivity');
  }

  /**
   * Get formatted time remaining
   */
  public getFormattedTimeRemaining(): string {
    const sessionInfo = this.getSessionInfo();
    if (sessionInfo.timeRemaining <= 0) return '0:00';

    const minutes = Math.floor(sessionInfo.timeRemaining / 60000);
    const seconds = Math.floor((sessionInfo.timeRemaining % 60000) / 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Update session configuration
   */
  public updateConfig(newConfig: Partial<SessionConfig>): void {
    this.config = { ...this.config, ...newConfig };

    // Restart monitoring with new config if currently active
    if (this.checkInterval) {
      this.stopSession();
      this.startSession(this.onSessionExpired, this.onSessionWarning);
    }
  }
}

// Export singleton instance
export const sessionManager = SessionManager.getInstance();

// Convenience functions
export const startSession = (
  onExpired?: () => void,
  onWarning?: (timeRemaining: number) => void
) => sessionManager.startSession(onExpired, onWarning);

export const stopSession = () => sessionManager.stopSession();
export const extendSession = () => sessionManager.extendSession();
export const getSessionInfo = () => sessionManager.getSessionInfo();
export const isSessionExpired = () => sessionManager.isSessionExpired();
export const isSessionExpiring = () => sessionManager.isSessionExpiring();
